import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useEnquiry } from "@/components/site/EnquiryProvider";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const headline = ["Paintings that", "frame the", "spirit of nature."];

export default function Hero() {
  const { openEnquiry } = useEnquiry();
  const sectionRef = useRef(null);
  const frameRef = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const onMove = (e) => {
    const el = frameRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: py * -8, ry: px * 10 });
  };
  const reset = () => setTilt({ rx: 0, ry: 0 });

  return (
    <section
      ref={sectionRef}
      id="top"
      className="relative min-h-screen w-full overflow-hidden pt-28 pb-16 md:pt-24 md:pb-0"
      data-testid="hero-section"
    >
      <div className="max-w-screen-2xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center min-h-[calc(100vh-7rem)]">
        {/* Text */}
        <motion.div style={{ y: textY }} className="md:col-span-6 lg:col-span-5 relative z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.25, duration: 0.8 }}
            className="font-body text-[11px] md:text-xs tracking-[0.35em] uppercase text-ochre mb-6"
          >
            Contemporary Fine Art &middot; Gurugram, India
          </motion.p>

          <h1 className="font-serif text-[2.9rem] leading-[1.04] sm:text-6xl lg:text-7xl text-ink tracking-tight">
            {headline.map((l, i) => (
              <span key={i} className="reveal-line">
                <motion.span
                  custom={i}
                  variants={line}
                  initial="hidden"
                  animate="show"
                  className="block pb-[0.05em]"
                >
                  {i === 2 ? (
                    <span>
                      spirit <span className="italic text-ochre">of nature.</span>
                    </span>
                  ) : (
                    l
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-8 max-w-md font-body text-base md:text-lg text-ink-soft leading-relaxed"
          >
            Original works by <span className="text-ink">Aparna Banerjee</span> &mdash;
            UNESCO Club&ndash;Athens certified artist with 100+ exhibitions worldwide.
            Bring home a canvas that breathes.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.3, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href="#collection"
              data-testid="hero-view-collection"
              data-cursor="Browse"
              className="group inline-flex items-center gap-3 bg-ink px-8 py-4 font-body text-xs tracking-[0.25em] uppercase text-paper transition-colors hover:bg-ochre"
            >
              View the Collection
              <span className="transition-transform group-hover:translate-x-1">&darr;</span>
            </a>
            <button
              onClick={() => openEnquiry(null)}
              data-testid="hero-enquire-button"
              className="font-body text-xs tracking-[0.25em] uppercase text-ink border-b border-ink pb-1 transition-colors hover:text-ochre hover:border-ochre"
            >
              Enquire to acquire
            </button>
          </motion.div>
        </motion.div>

        {/* Painting */}
        <div className="md:col-span-6 lg:col-span-7 relative flex justify-center md:justify-end">
          <motion.div
            ref={frameRef}
            onMouseMove={onMove}
            onMouseLeave={reset}
            initial={{ opacity: 0, scale: 0.92 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            style={{ perspective: 1000 }}
            className="relative w-full max-w-[520px]"
            data-cursor="Tree of Life"
          >
            <motion.div
              style={{
                rotateX: tilt.rx,
                rotateY: tilt.ry,
                transformStyle: "preserve-3d",
              }}
              transition={{ type: "spring", stiffness: 120, damping: 18 }}
              className="relative"
            >
              <div className="relative overflow-hidden gallery-shadow border-[6px] border-paper bg-sand aspect-[4/5]">
                <motion.img
                  src="/artworks/premium/tree-of-life.jpeg"
                  alt="Tree of Life – Pomegranates by Aparna Banerjee"
                  style={{ y: imgY, scale: imgScale }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                {/* spotlight */}
                <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_50%_35%,transparent_35%,rgba(44,36,30,0.28)_100%)]" />
              </div>

              <div className="absolute -bottom-5 -left-4 bg-paper border border-ink-soft/20 px-5 py-3 gallery-shadow">
                <p className="font-serif text-lg leading-none text-ink">Tree of Life</p>
                <p className="font-body text-[10px] tracking-[0.2em] uppercase text-ink-soft mt-1">
                  Acrylics &middot; 4 &times; 4 ft
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2"
      >
        <span className="font-body text-[10px] tracking-[0.3em] uppercase text-ink-soft">Scroll</span>
        <span className="h-10 w-px bg-ink-soft/40 overflow-hidden relative">
          <motion.span
            animate={{ y: ["-100%", "100%"] }}
            transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
            className="absolute inset-0 bg-ochre"
          />
        </span>
      </motion.div>
    </section>
  );
}
